/**
 * useTimeFilter — manages the selected time preset (+ custom range) with URL
 * persistence, so a dashboard's period survives refresh/share/back-navigation.
 *
 * State lives in the URL query string (react-router v7 useSearchParams). A
 * `paramPrefix` lets multiple independent filters coexist on one page.
 *
 * Returns:
 *   preset, dateFrom, dateTo  — raw current values
 *   time                      — the object to send to /api/dashboard/v2/kpis
 *   setFilter({preset,dateFrom,dateTo}) — partial update
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const DEFAULT_PRESET = 'last_30_days';

export function useTimeFilter({ paramPrefix = '', defaultPreset = DEFAULT_PRESET } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const key = useCallback((name) => `${paramPrefix}${name}`, [paramPrefix]);

  const preset = searchParams.get(key('preset')) || defaultPreset;
  const dateFrom = searchParams.get(key('from')) || '';
  const dateTo = searchParams.get(key('to')) || '';

  const setFilter = useCallback(
    (next) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          const nextPreset = next.preset ?? preset;
          sp.set(key('preset'), nextPreset);

          if (nextPreset === 'custom') {
            const from = next.dateFrom ?? dateFrom;
            const to = next.dateTo ?? dateTo;
            from ? sp.set(key('from'), from) : sp.delete(key('from'));
            to ? sp.set(key('to'), to) : sp.delete(key('to'));
          } else {
            // Non-custom presets compute their own range server-side.
            sp.delete(key('from'));
            sp.delete(key('to'));
          }
          return sp;
        },
        { replace: true },
      );
    },
    [setSearchParams, key, preset, dateFrom, dateTo],
  );

  const time = useMemo(() => {
    if (preset === 'custom') {
      return { preset, date_from: dateFrom || null, date_to: dateTo || null };
    }
    return { preset };
  }, [preset, dateFrom, dateTo]);

  return { preset, dateFrom, dateTo, time, setFilter };
}

export default useTimeFilter;
